// What each provider's API actually accepts for the sampling dials.
//
// These are NOT cosmetic hints. The ranges differ between providers, and the
// difference is a 400 rather than a clamp: Anthropic's Messages API rejects
// any temperature above 1.0, while OpenAI and Gemini accept up to 2.0. A
// single `0–2` control is therefore wrong for two of the five providers, and
// wrong in the direction that only shows up as a failed reply at chat time,
// long after the admin left the settings screen.
//
// `default` is the value the provider's own API uses when the parameter is
// OMITTED — which is what a blank field means here (see the orchestrator's
// samplingOpts: a null is left out of the request entirely, and that is not
// the same as sending 0). Showing it turns a blank box from "nothing" into
// "1 — the provider's default", which is the number actually in effect.
//
// It is null where the effective default is not ours to state: `openline`
// serves Ollama models whose own Modelfile sets it, and `custom` is somebody
// else's gateway.

import type { Provider } from './types';

export interface SamplingBound {
  min: number;
  max: number;
  /** Input granularity — 0.01 everywhere the API takes two decimals. */
  step: number;
  /** The API's value when the parameter is omitted, or null if provider-defined. */
  default: number | null;
}

export interface SamplingLimits {
  temperature: SamplingBound;
  topP: SamplingBound;
}

// Nucleus sampling is 0–1 for every provider here — it is a probability mass,
// so there is no room for disagreement about the range, only the default.
const TOP_P = (dflt: number | null): SamplingBound => ({
  min: 0,
  max: 1,
  step: 0.01,
  default: dflt,
});

export const AI_SAMPLING_LIMITS: Readonly<Record<Provider, SamplingLimits>> = {
  // Messages API: temperature 0.0–1.0. Above 1.0 is a 400, not a clamp.
  anthropic: {
    temperature: { min: 0, max: 1, step: 0.01, default: 1 },
    // Anthropic applies no nucleus cut-off unless top_p is sent, so there is
    // no default value to quote — omitting it is its own behaviour.
    topP: TOP_P(null),
  },
  // Chat Completions: 0–2, default 1 for both dials.
  openai: {
    temperature: { min: 0, max: 2, step: 0.01, default: 1 },
    topP: TOP_P(1),
  },
  // generateContent: 0–2 on current models, default 1. topP defaults to 0.95.
  gemini: {
    temperature: { min: 0, max: 2, step: 0.01, default: 1 },
    topP: TOP_P(0.95),
  },
  // Ollama-backed gateway. The range is wide, but the effective default comes
  // from the served model's own Modelfile, which we cannot read from here.
  openline: {
    temperature: { min: 0, max: 2, step: 0.01, default: null },
    topP: TOP_P(null),
  },
  // Someone else's OpenAI-compatible endpoint: assume the OpenAI range, claim
  // no default.
  custom: {
    temperature: { min: 0, max: 2, step: 0.01, default: null },
    topP: TOP_P(null),
  },
};

/** Limits for `provider`, falling back to the widest safe range if unknown. */
export function samplingLimits(provider: string): SamplingLimits {
  return (
    AI_SAMPLING_LIMITS[provider as Provider] ?? AI_SAMPLING_LIMITS.custom
  );
}

/**
 * Why `value` is not acceptable for `provider`, or null when it is. Used by
 * both the admin form and the write endpoint so the two agree on the rule
 * instead of the server silently accepting what the UI just warned about.
 */
export function samplingOutOfRange(
  provider: string,
  field: 'temperature' | 'topP',
  value: number | null | undefined,
): string | null {
  if (value === null || value === undefined) return null;
  const bound = samplingLimits(provider)[field];
  if (!Number.isFinite(value)) return `${field} must be a number.`;
  if (value < bound.min || value > bound.max) {
    return `${provider} accepts ${field} between ${bound.min} and ${bound.max}; got ${value}.`;
  }
  return null;
}
